import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../../constants";
import {
  checkVerdict,
  MicrosoftConnectionCheck,
} from "../use-microsoft-connection-check";
import { SECONDARY_BUTTON, SettingsCard, StatusBanner } from "../ui";

type Provider = "s3" | "google" | "microsoft";

interface Usage {
  provider: Provider;
  objects: number;
  bytes: number;
  quotaBytes: number | null;
  quotaUsedPercent: number | null;
  // Files kept by the CURRENT provider (ours, Drive or OneDrive). Absent on a
  // backend that predates it.
  files?: { count: number; bytes: number; unknownSizeCount: number };
}

// What the person reads for each way the Microsoft connection check can fail,
// and how serious it is. A reason this table does not know (a newer server)
// reads like REQUEST_FAILED.
function formatBytes(bytes: number): string {
  if (!bytes) return "0 MB";
  const mb = bytes / 1024 / 1024;
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

/**
 * Where this company's documents are kept.
 *
 * The point of this screen is the first line: a person should be able to open
 * it and know, without reading anything else, whose disk their documents are
 * sitting on. Everything below is detail.
 */
export default function DocumentStorageTab({
  connectionCheck,
}: {
  connectionCheck: MicrosoftConnectionCheck;
}) {
  const { t } = useTranslation();
  const { companyId } = useParams();

  const [usage, setUsage] = useState<Usage | null>(null);
  const [loading, setLoading] = useState(true);
  const [googleClientId, setGoogleClientId] = useState("");
  const [googleSecret, setGoogleSecret] = useState("");
  const [googleConnected, setGoogleConnected] = useState(false);
  // The Google Drive setup is for the few companies that use it; everyone else
  // would only see a form they cannot use. It opens by itself when Google is
  // the current provider, and on demand otherwise.
  const [showGoogleSetup, setShowGoogleSetup] = useState(false);
  const [busy, setBusy] = useState(false);
  // Two error slots, not one. `loadError` belongs to the usage request and is
  // cleared when it succeeds; `error` belongs to what the person just did
  // (switching provider, returning from the Google consent screen). Sharing a
  // slot meant a successful usage load silently wiped "Google was not
  // connected" a moment after the OAuth redirect set it.
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/${companyId}/storage/usage`, {
        withCredentials: true,
      });
      setUsage(data);
      setLoadError(null);
    } catch (e) {
      setUsage(null);
      setLoadError(t("storage_load_failed"));
    } finally {
      setLoading(false);
    }
  }, [companyId, t]);

  useEffect(() => {
    load();
  }, [load]);

  // The OAuth callback returns here with a marker so the person sees the result
  // of what they just did rather than an unchanged screen.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("googleConnected");
    // Strip it immediately: the tab remounts whenever someone comes back to it,
    // and a leftover flag would replay "connected" as if it had just happened.
    if (flag !== null) {
      params.delete("googleConnected");
      const rest = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (rest ? `?${rest}` : "")
      );
    }
    if (flag === "1") {
      setNotice(t("storage_google_connected"));
      setGoogleConnected(true);
      load();
    } else if (flag === "0") {
      setError(t("storage_google_connect_failed"));
      setShowGoogleSetup(true);
    }
  }, [load, t]);

  const switchProvider = async (provider: Provider) => {
    if (!companyId) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await axios.patch(
        `${API_URL}/${companyId}/storage/provider`,
        { provider },
        { withCredentials: true }
      );
      setNotice(t("storage_provider_changed"));
      await load();
    } catch (e: any) {
      // The API refuses a backend that is not connected; say which, rather
      // than letting it fail later on someone's upload.
      const code = e?.response?.data;
      if (code === "GOOGLE_NOT_CONNECTED") setShowGoogleSetup(true);
      setError(
        code === "GOOGLE_NOT_CONNECTED"
          ? t("storage_google_not_connected")
          : code === "MICROSOFT_NOT_CONNECTED"
          ? t("storage_microsoft_not_connected")
          : code === "S3_NOT_CONFIGURED"
          ? t("storage_s3_not_configured")
          : t("storage_provider_change_failed")
      );
    } finally {
      setBusy(false);
    }
  };

  const saveGoogleApp = async () => {
    if (!companyId) return;
    setBusy(true);
    setError(null);
    try {
      await axios.patch(
        `${API_URL}/${companyId}/google-settings`,
        { clientId: googleClientId, clientSecret: googleSecret },
        { withCredentials: true }
      );
      setNotice(t("storage_google_app_saved"));
    } catch (e) {
      setError(t("storage_google_app_save_failed"));
    } finally {
      setBusy(false);
    }
  };

  const connectGoogle = async () => {
    if (!companyId) return;
    try {
      const { data } = await axios.get(
        `${API_URL}/${companyId}/google/auth-url`,
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (e) {
      setError(t("storage_google_app_required"));
    }
  };

  // Only claim a location once one is actually known. Falling back to "ours"
  // while loading, or after a failed load, would state the wrong answer with
  // full confidence — and stating where a client's documents are is the whole
  // point of this screen.
  const current = usage?.provider ?? null;
  // ONE number for the bar, the label and the warning — the server also sends
  // quotaUsedPercent, but showing two independently derived figures on the same
  // line is how they end up disagreeing.
  //
  // "Full" is a byte comparison, never a rounded one: at 99.6% a rounded
  // percentage reads 100 and the screen would tell someone their uploads are
  // being refused while they still have room. So the label stops at 99% until
  // the bytes actually reach the limit.
  const quotaBytes = usage?.quotaBytes || 0;
  const isOverQuota = !!usage && quotaBytes > 0 && usage.bytes >= quotaBytes;
  const fillRatio = usage && quotaBytes > 0 ? usage.bytes / quotaBytes : 0;
  const percentUsed = isOverQuota
    ? 100
    : Math.min(99, Math.floor(fillRatio * 100));
  const isNearlyFull = !isOverQuota && fillRatio >= 0.9;
  const isGoogleSetupVisible = current === "google" || showGoogleSetup;
  const whereText =
    current === "s3"
      ? t("storage_where_ours")
      : current === "google"
      ? t("storage_where_google")
      : current === "microsoft"
      ? t("storage_where_microsoft")
      : null;

  // The check is about OneDrive: on any other storage it says nothing about
  // where the documents are, so it neither shows nor colours the banner.
  const verdict =
    current === "microsoft" && connectionCheck.result
      ? checkVerdict(connectionCheck.result)
      : null;
  // The banner is green while all is well; a failed check makes it say so.
  const bannerTone = verdict?.tone ?? (current ? "ok" : "info");

  return (
    <div className="space-y-5">
      {/* The answer, before any controls — and only when it is known. */}
      <StatusBanner
        tone={bannerTone}
        title={
          whereText ?? (loading ? t("storage_loading") : t("storage_unknown"))
        }
        detail={
          <>
            {usage?.files && (
              <p>
                {t("storage_files_count", { count: usage.files.count })} ·{" "}
                {formatBytes(usage.files.bytes)}
                {usage.files.unknownSizeCount > 0 &&
                  ` (${t("storage_files_unknown_size", {
                    count: usage.files.unknownSizeCount,
                  })})`}
              </p>
            )}
            {current === "s3" && usage && (
              <div className="mt-2">
                <p>
                  {t("storage_used")}:{" "}
                  <strong>{formatBytes(usage.bytes)}</strong>
                  {usage.quotaBytes
                    ? ` ${t("storage_of")} ${formatBytes(
                        usage.quotaBytes
                      )} (${percentUsed}%)`
                    : ""}
                </p>
                {/* Only with a limit to fill: without one a bar has no meaning,
                    and a full-looking bar would be alarming for no reason. */}
                {usage.quotaBytes ? (
                  <>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                      <div
                        className={`h-full rounded-full ${
                          isOverQuota
                            ? "bg-red-500"
                            : isNearlyFull
                            ? "bg-amber-500"
                            : "bg-indigo-500"
                        }`}
                        style={{ width: `${percentUsed}%` }}
                      />
                    </div>
                    {isOverQuota ? (
                      <p className="mt-2 font-medium text-red-700">
                        {t("storage_quota_full")}
                      </p>
                    ) : isNearlyFull ? (
                      <p className="mt-2 text-amber-800">
                        {t("storage_quota_nearly_full")}
                      </p>
                    ) : null}
                  </>
                ) : null}
              </div>
            )}
            {current && current !== "s3" && (
              <p className="mt-2">{t("storage_own_cloud_note")}</p>
            )}
            {verdict && (
              <p className="mt-2 font-medium">{t(verdict.textKey)}</p>
            )}
          </>
        }
        action={
          current === "microsoft" ? (
            <button
              type="button"
              onClick={connectionCheck.run}
              disabled={connectionCheck.checking}
              className={SECONDARY_BUTTON}
            >
              {connectionCheck.checking
                ? t("storage_loading")
                : t("settings_ms_check_connection")}
            </button>
          ) : undefined
        }
      />

      {notice && <StatusBanner tone="ok" title={notice} />}
      {error && <StatusBanner tone="error" title={error} />}
      {loadError && <StatusBanner tone="error" title={loadError} />}

      <SettingsCard
        title={t("storage_choose")}
        description={t("storage_switch_note")}
      >
        <div className="flex flex-wrap gap-2">
          {(["s3", "google", "microsoft"] as Provider[]).map((p) => (
            <button
              key={p}
              type="button"
              disabled={busy || loading || current === p}
              onClick={() => switchProvider(p)}
              className={`min-h-11 rounded-md border px-3 py-2 text-sm md:min-h-0 [@media(pointer:coarse)]:min-h-11 ${
                current === p
                  ? "border-indigo-500 bg-indigo-600 text-white"
                  : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
              } disabled:opacity-60`}
            >
              {p === "s3"
                ? t("storage_option_ours")
                : p === "google"
                ? t("storage_option_google")
                : t("storage_option_microsoft")}
            </button>
          ))}
        </div>
        {!isGoogleSetupVisible && (
          <button
            type="button"
            onClick={() => setShowGoogleSetup(true)}
            className="mt-3 text-xs text-indigo-600 underline hover:text-indigo-500"
          >
            {t("storage_google_section")}
          </button>
        )}
      </SettingsCard>

      {isGoogleSetupVisible && (
        <SettingsCard
          title={t("storage_google_section")}
          description={t("storage_google_scope_note")}
        >
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t("client_id")}
              </label>
              <input
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-base md:text-sm"
                value={googleClientId}
                onChange={(e) => setGoogleClientId(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t("secret")}
              </label>
              <input
                type="password"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-base md:text-sm"
                value={googleSecret}
                onChange={(e) => setGoogleSecret(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy || !googleClientId || !googleSecret}
                onClick={saveGoogleApp}
                className="min-h-11 rounded-md bg-gray-800 px-3 py-2 text-sm text-white md:min-h-0 [@media(pointer:coarse)]:min-h-11 disabled:opacity-60"
              >
                {t("save")}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={connectGoogle}
                className="min-h-11 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 md:min-h-0 [@media(pointer:coarse)]:min-h-11 disabled:opacity-60"
              >
                {googleConnected
                  ? t("storage_google_reconnect")
                  : t("storage_google_connect")}
              </button>
            </div>
          </div>
        </SettingsCard>
      )}
    </div>
  );
}
